import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, X, SlidersHorizontal, ArrowLeft, Clock } from "lucide-react";
import { RestaurantCard } from "@/components/customer/RestaurantCard";
import { EmptyState, SkelCard, VegDot } from "@/components/common";
import { api, inr } from "@/lib/api";

const SORTS = [
  { k: "", l: "Recommended" }, { k: "rating", l: "Rating" }, { k: "delivery_time", l: "Fastest" },
  { k: "distance", l: "Nearest" }, { k: "price_low", l: "Price ↑" }, { k: "price_high", l: "Price ↓" },
];

export default function SearchPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const cat = params.get("cat") || "";
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popular, setPopular] = useState([]);
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem("bitego_recent") || "[]"); } catch { return []; } });
  const [filters, setFilters] = useState({ veg: false, offers: false, open_now: false, sort: "", max_time: 0, min_rating: 0 });
  const [catRests, setCatRests] = useState(null);

  useEffect(() => { api.get("/search/meta").then((r) => setPopular(r.data.popular)).catch(() => {}); }, []);

  useEffect(() => {
    if (!cat) { setCatRests(null); return; }
    const p = { category: cat };
    if (filters.veg) p.veg = true; if (filters.offers) p.offers = true; if (filters.open_now) p.open_now = true;
    if (filters.sort) p.sort = filters.sort; if (filters.max_time) p.max_time = filters.max_time; if (filters.min_rating) p.min_rating = filters.min_rating;
    api.get("/restaurants", { params: p }).then((r) => setCatRests(r.data)).catch(() => {});
  }, [cat, filters]);

  useEffect(() => {
    if (cat) return;
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults(null); return; }
      setLoading(true);
      try { const { data } = await api.get("/search", { params: { q } }); setResults(data); } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, cat]);

  const saveRecent = (term) => {
    const next = [term, ...recent.filter((x) => x !== term)].slice(0, 8);
    setRecent(next); localStorage.setItem("bitego_recent", JSON.stringify(next));
  };

  const toggle = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));

  const showResults = cat ? catRests : results;

  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto pb-24">
      <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3">
        <div className="flex items-center gap-2">
          <button onClick={() => nav(-1)} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
          <div className="flex-1 flex items-center gap-2 bg-surface rounded-xl px-3 py-2.5">
            <SearchIcon size={18} className="text-coral" />
            <input autoFocus={!cat} value={cat || q} onChange={(e) => setQ(e.target.value)} placeholder="Search restaurants, dishes, cuisines" className="flex-1 min-w-0 bg-transparent text-sm outline-none" data-testid="search-input" disabled={!!cat} />
            {(q || cat) && <button onClick={() => (cat ? nav("/search") : setQ(""))}><X size={18} className="text-ink-600" /></button>}
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto hide-scrollbar mt-3">
          {SORTS.map((s) => (
            <button key={s.k} onClick={() => setFilters((f) => ({ ...f, sort: s.k }))} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${filters.sort === s.k ? "bg-ink text-white border-ink" : "bg-white border-border text-ink-600"}`} data-testid={`sort-${s.k || "rec"}`}>{s.l}</button>
          ))}
          <button onClick={() => toggle("veg")} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${filters.veg ? "bg-veg text-white border-veg" : "bg-white border-border text-ink-600"}`} data-testid="filter-veg">Pure Veg</button>
          <button onClick={() => toggle("offers")} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${filters.offers ? "bg-coral text-white border-coral" : "bg-white border-border text-ink-600"}`} data-testid="filter-offers">Offers</button>
          <button onClick={() => toggle("open_now")} className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border ${filters.open_now ? "bg-coral text-white border-coral" : "bg-white border-border text-ink-600"}`} data-testid="filter-open">Open Now</button>
        </div>
      </div>

      <div className="px-4 py-4">
        {cat && <h2 className="font-display font-bold text-lg text-ink mb-3">{cat}</h2>}

        {!q && !cat && (
          <>
            {recent.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2"><h3 className="font-bold text-sm text-ink">Recent searches</h3><button onClick={() => { setRecent([]); localStorage.removeItem("bitego_recent"); }} className="text-xs text-coral font-semibold">Clear all</button></div>
                <div className="flex flex-wrap gap-2">{recent.map((t) => <button key={t} onClick={() => setQ(t)} className="flex items-center gap-1 bg-white border border-border rounded-full px-3 py-1.5 text-sm"><Clock size={13} className="text-ink-600" />{t}</button>)}</div>
              </div>
            )}
            <h3 className="font-bold text-sm text-ink mb-2">Popular searches</h3>
            <div className="flex flex-wrap gap-2">{popular.map((t) => <button key={t} onClick={() => { setQ(t); saveRecent(t); }} className="bg-coral-light text-coral font-semibold rounded-full px-3 py-1.5 text-sm tap" data-testid={`popular-search-${t}`}>{t}</button>)}</div>
          </>
        )}

        {loading && <div className="flex gap-3 overflow-hidden">{[0, 1].map((i) => <SkelCard key={i} />)}</div>}

        {showResults && (
          <div className="space-y-6">
            {showResults.dishes?.length > 0 && (
              <div>
                <h3 className="font-bold text-sm text-ink mb-2">Dishes</h3>
                <div className="space-y-2">
                  {showResults.dishes.map((d) => (
                    <button key={d.id} onClick={() => nav(`/restaurant/${d.restaurant_id}`)} className="w-full flex items-center gap-3 bg-white rounded-xl p-2.5 border border-border tap text-left" data-testid={`dish-result-${d.id}`}>
                      <img src={d.image} alt="" className="w-14 h-14 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0"><div className="flex items-center gap-1.5"><VegDot veg={d.veg} /><span className="font-bold text-sm truncate">{d.name}</span></div><div className="text-xs text-ink-600 truncate">{d.restaurant_name} · {inr(d.price)}</div></div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(showResults.restaurants || showResults)?.length !== 0 && (
              <div>
                <h3 className="font-bold text-sm text-ink mb-2">Restaurants</h3>
                <div className="grid gap-4">
                  {(Array.isArray(showResults) ? showResults : showResults.restaurants).map((r) => <RestaurantCard key={r.id} r={r} wide />)}
                </div>
              </div>
            )}
            {((Array.isArray(showResults) ? showResults : showResults.restaurants)?.length === 0 && (showResults.dishes?.length || 0) === 0) && (
              <EmptyState icon={SearchIcon} title="No results found" subtitle="Try a different dish, cuisine or restaurant name." />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
